import React, { FC, useMemo } from 'react';

import cls from 'classnames';

import ApplePayCardIcon from '@ott/icon-apple-pay-card';
import CryptoIcon from '@ott/icon-crypto';
import CryptoComIcon from '@ott/icon-crypto-com';
import DefaultCardIcon from '@ott/icon-default-card';
import GooglePayCardIcon from '@ott/icon-google-pay-card';
import MaestroCardIcon from '@ott/icon-maestro-card';
import MasterCardIcon from '@ott/icon-mastercard-card';
import MetamaskIcon from '@ott/icon-metamask';
import MirCardIcon from '@ott/icon-mir-card';
import PayPalIcon from '@ott/icon-paypal';
import QiwiIcon from '@ott/icon-qiwi';
import SberPayIcon from '@ott/icon-sber-pay';
import SbpIcon from '@ott/icon-sbp';
import VisaCardIcon from '@ott/icon-visa-card';
import WalletConnectIcon from '@ott/icon-wallet-connect';
import YooMoneyIcon from '@ott/icon-yoo-money';
import { lang } from '@ott/l10n';
import RadioGroup from '@ott/radio-group';

import VirtualCardLabel from './blocks/VirtualCardLabel';
import { Variant } from './types';

import styles from './PaymentVariants.scss';

type Props = {
  /** Передаем значение текущего активного айтема, для того чтобы отрисовать его выбранным */
  value: string | number | boolean;
  /** Массив значений для отрисовки в радио группе */
  variants: Variant[];
  /** Колбэк срабатывающий при выборе какого-либо айтема радио группы, возвращающий значение айтема */
  onChange: (value: string | number | boolean) => void;
  /** Передаем свой класс для управления стилем самого компонента, опционально */
  className?: string;
  /** Для случаев когда нужно отдельно показать только вариант "Новая карта" */
  allowSingleVaraint?: boolean;
};

const getIcons = (variant: Variant) => {
  const { icons } = variant;
  if (!icons || !icons.length) {
    return null;
  }

  return icons.map((icon) => {
    if (typeof icon !== 'string') {
      return icon;
    }
    switch (icon) {
      case 'mastercard':
        return <MasterCardIcon key={icon} className={styles.icon} />;
      case 'maestro':
        return <MaestroCardIcon key={icon} className={styles.icon} />;
      case 'visa':
        return <VisaCardIcon key={icon} className={styles.icon} />;
      case 'mir':
        return <MirCardIcon key={icon} className={styles.icon} />;
      case 'paypal':
        return <PayPalIcon key={icon} w={75} h={24} className={styles.icon} />;
      case 'qiwi':
        return <QiwiIcon key={icon} w={56} h={24} className={styles.icon} />;
      case 'yandex':
        return <YooMoneyIcon key={icon} w={86} h={18} className={styles.icon} />;
      case 'googlePay':
        return <GooglePayCardIcon key={icon} w={34} h={24} className={styles.icon} />;
      case 'applePay':
        return <ApplePayCardIcon key={icon} w={34} h={24} className={styles.icon} />;
      case 'sbp':
        return <SbpIcon key={icon} w={44} h={24} className={styles.icon} />;
      case 'sberPay':
        return <SberPayIcon key={icon} w={50} h={24} className={styles.icon} />;
      case 'crypto':
        return <CryptoIcon key={icon} className={styles.icon} />;
      case 'cryptoCom':
        return <CryptoComIcon key={icon} className={styles.icon} />;
      case 'walletConnect':
        return <WalletConnectIcon key={icon} className={styles.icon} />;
      case 'metamask':
        return <MetamaskIcon key={icon} className={styles.icon} />;
      case 'default':
        return <DefaultCardIcon key={icon} w={34} h={24} className={styles.icon} />;

      default:
        return null;
    }
  });
};

const getLabel = (variant: Variant) => {
  const { title, text, disabled, virtualCard } = variant;

  if (virtualCard) {
    return (
      <VirtualCardLabel
        currency={virtualCard.currency}
        isMoneyShown={virtualCard.isMoneyShown}
        kmParams={virtualCard.kmParams}
        total={virtualCard.total}
        withQuickAuthorization={virtualCard.withQuickAuthorization}
        disabled={disabled}
      />
    );
  }

  let titleNode: React.ReactNode = null;
  if (title) {
    if (typeof title === 'string') {
      titleNode = <div className={styles.title} dangerouslySetInnerHTML={{ __html: title }} />;
    } else {
      titleNode = <div className={styles.title}>{title}</div>;
    }
  }

  let textNode: React.ReactNode = null;
  if (text) {
    if (typeof text === 'string') {
      textNode = <div className={styles.text} dangerouslySetInnerHTML={{ __html: text }} />;
    } else {
      textNode = <div className={styles.text}>{text}</div>;
    }
  }

  return (
    <div className={cls(styles.label, disabled && styles['label--isDisabled'])}>
      <div className={styles.wrap}>
        {titleNode}
        <div className={styles.icons}>{getIcons(variant)}</div>
      </div>
      {textNode}
    </div>
  );
};

const PaymentVariants: FC<Props> = (props: Props) => {
  const { variants, value, onChange, className, allowSingleVaraint = false }: Props = props;

  const preparedVariants = useMemo(() => {
    return variants
      .filter((variant) => (lang !== 'ru' ? !variant.virtualCard : true))
      .map((item) => {
        const { value: variantValue, disabled } = item;
        const locator = `payment-variant-${variantValue}`.replace(/_/g, '-');
        return {
          specificLocators: ['payment-variant-row', locator],
          value: variantValue,
          isDisabled: disabled,
          label: getLabel(item),
        };
      });
  }, [variants]);

  if (preparedVariants.length === 1 && !allowSingleVaraint) {
    return null;
  }

  return (
    <RadioGroup
      value={value}
      className={cls(styles.PaymentVariants, className)}
      childrenClassNames={{ label: styles.radioGroupLabel }}
      name="payment-variants"
      items={preparedVariants}
      onChange={onChange}
    />
  );
};

export default PaymentVariants;
